import { useUserIntegrations } from "@pages/account-settings/integrations-account/use-user-integrations";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { IntegrationRow } from "@pages/account-settings/integrations-account/integration-row";
import { Divider, Stack } from "@mui/material";
import { useState } from "react";
import { IntegrationSettingsSkeleton } from "@pages/account-settings/integrations-account/integration-settings-skeleton";
import { IntegrationDialog } from "@components/integrations-dialog/integration-dialog";
import { useTokensService } from "@context-providers/tokens-service-provider";

/**
 * Displays a list of available user-level integrations.
 * It handles enabling and disabling integrations and manages dialog states related to integration actions.
 */
export function IntegrationList(): JSX.Element {
  const tokensService = useTokensService();
  const [integrationId, setIntegrationId] =
    useState<SphereDashboardAPITypes.IntegrationId>(
      SphereDashboardAPITypes.IntegrationId.autodesk
    );

  const {
    allIntegrations,
    onEnableIntegration,
    dialogState,
    setDialogState,
    onSuccessDialogConfirm,
    onInProgressDialogCancel,
    onDisconnectDialogConfirm,
    onDisconnectDialogCancel,
    isLoadingIntegrations,
  } = useUserIntegrations({
    integrationId,
    tokensService,
  });

  if (isLoadingIntegrations) {
    return <IntegrationSettingsSkeleton />;
  }

  return (
    <Stack width={"100%"}>
      {allIntegrations.availableIntegrationIds.map((integrationId, index) => (
        <Stack key={integrationId}>
          <IntegrationRow
            integrationId={integrationId}
            enabledIntegrationIds={allIntegrations.enabledIntegrationIds}
            onEnable={(integrationId) => {
              setIntegrationId(integrationId);
              void onEnableIntegration(integrationId);
            }}
            onDisable={(integrationId) => {
              setIntegrationId(integrationId);
              setDialogState("disconnect");
            }}
          />
          {index !== allIntegrations.availableIntegrationIds.length - 1 && (
            <Divider />
          )}
        </Stack>
      ))}

      <IntegrationDialog
        integrationId={integrationId}
        dialogState={dialogState}
        onSuccessDialogConfirm={onSuccessDialogConfirm}
        onInProgressDialogCancel={onInProgressDialogCancel}
        onDisconnectDialogConfirm={onDisconnectDialogConfirm}
        onDisconnectDialogCancel={onDisconnectDialogCancel}
      />
    </Stack>
  );
}
